import { useAccessTokens, useCreateAccessToken, useRevokeAccessToken } from '@/hooks/useAccessTokens';
import { CreatedAccessToken, AccessToken } from '@/types/auth';
import { errorMessage, successMessage } from '@/utils/toast';
import DatePicker from '@components/DatePicker';
import ClickableTooltip from '@components/ClickableTooltip';
import { Button, Dialog, DialogBackdrop, DialogPanel, DialogTitle, Field, Input, Label } from '@headlessui/react';
import { ClipboardDocumentIcon, InformationCircleIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { isAxiosError } from 'axios';
import { t } from 'i18next';
import { FormEvent, useState } from 'react';

function formatDate(value: string | null) {
    if (!value) return t('never');
    return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value));
}

function isExpired(value: string | null) {
    return !!value && new Date(value).getTime() <= Date.now();
}

export default function ManageApiTokens() {
    const { data: tokens = [], isLoading } = useAccessTokens();
    const create = useCreateAccessToken();
    const revoke = useRevokeAccessToken();
    const [createOpen, setCreateOpen] = useState(false);
    const [createdToken, setCreatedToken] = useState<CreatedAccessToken | null>(null);
    const [tokenToRevoke, setTokenToRevoke] = useState<AccessToken | null>(null);
    const [copied, setCopied] = useState(false);
    const [expirationDate, setExpirationDate] = useState<Date | null>(null);

    function submitCreate(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();
        const formElement = event.currentTarget;
        const form = new FormData(formElement);
        create.mutate({
            tokenName: String(form.get('tokenName') || ''),
            expiresAt: expirationDate?.toISOString() || null,
            password: String(form.get('password') || ''),
        }, {
            onSuccess: (response) => {
                setCreateOpen(false);
                setCreatedToken(response.data.data);
                setCopied(false);
                setExpirationDate(null);
                formElement.reset();
                successMessage(t('status:success.apiTokenCreated'));
            },
        });
    }

    function submitRevoke(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();
        if (!tokenToRevoke) return;
        const form = new FormData(event.currentTarget);
        revoke.mutate({ id: tokenToRevoke.id, password: String(form.get('password') || '') }, {
            onSuccess: () => {
                setTokenToRevoke(null);
                successMessage(t('status:success.apiTokenRevoked'));
            },
        });
    }

    async function copyToken() {
        if (!createdToken) return;
        try {
            await navigator.clipboard.writeText(createdToken.token);
            setCopied(true);
        } catch (error) {
            errorMessage(error);
        }
    }

    return (
        <section>
            <div className="grid grid-cols-1 gap-x-8 gap-y-8 md:grid-cols-10">
                <div className="px-4 sm:px-0 md:col-span-4">
                    <h2 className="text-lg font-semibold leading-7 text-gray-700">{t('apiTokens')}</h2>
                    <p className="mt-3 text-sm">{t('content:apiTokensDescription', 'Create tokens for external tools that need access to your product APIs.')}</p>
                </div>
                <div className="md:col-span-6">
                    <div className="flex justify-end">
                        <Button className="btn btn-primary" onClick={() => setCreateOpen(true)}>
                            <PlusIcon className="size-4" aria-hidden="true" />
                            {t('createApiToken')}
                        </Button>
                    </div>
                    <div className="mt-6 overflow-x-auto rounded-md border border-gray-200">
                        {isLoading ? <p className="p-4 text-sm text-gray-500">{t('loading')}</p> : tokens.length === 0 ? (
                            <p className="p-4 text-sm text-gray-500">{t('noApiTokens')}</p>
                        ) : <table className="min-w-full divide-y divide-gray-200 text-left text-sm">
                            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                                <tr>
                                    <th scope="col" className="px-4 py-3 font-medium">{t('tokenName')}</th>
                                    <th scope="col" className="px-4 py-3 font-medium">{t('created')}</th>
                                    <th scope="col" className="px-4 py-3 font-medium">{t('expires')}</th>
                                    <th scope="col" className="px-4 py-3 text-right font-medium">{t('actions')}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 bg-white">
                                {tokens.map(token => <TokenRow key={token.id} token={token} onRevoke={() => setTokenToRevoke(token)} />)}
                            </tbody>
                        </table>}
                    </div>
                </div>
            </div>

            <Dialog open={createOpen} onClose={() => setCreateOpen(false)} className="relative z-50">
                <DialogBackdrop className="fixed inset-0 bg-black/30" />
                <div className="fixed inset-0 flex items-center justify-center p-4">
                    <DialogPanel className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
                        <DialogTitle className="text-lg font-semibold text-gray-700">{t('createApiToken')}</DialogTitle>
                        <form className="mt-5 grid gap-4" onSubmit={submitCreate}>
                            <Field><Label className="input-label">{t('tokenName')}</Label><Input name="tokenName" required className="input mt-1" autoFocus /></Field>
                            <Field><DatePicker manualInput label={t('tokenExpiration')} selectedDate={expirationDate} setSelectedDate={setExpirationDate} /></Field>
                            <Field>
                                <div className="flex items-center gap-1">
                                    <Label className="input-label">{t('password')}</Label>
                                    <ClickableTooltip
                                        btn={<InformationCircleIcon className="size-4 text-gray-500" aria-label={t('information')} />}
                                        body={t('content:apiTokenPasswordExplanation', 'Your password is required to confirm that you are intentionally creating or revoking an API token. API tokens can grant access to your product data, so this extra check helps protect your account if someone else gains access to your open session.')}
                                    />
                                </div>
                                <Input name="password" type="password" required className="input mt-1" autoComplete="current-password" />
                            </Field>
                            <p className="text-sm text-gray-500">{t('content:apiTokenShownOnce', 'This token will be shown only once. Store it in a secure password manager.')}</p>
                            {isAxiosError(create.error) && <p className="text-sm text-red-600">{create.error.response?.data?.message}</p>}
                            <div className="flex justify-end gap-3"><Button type="button" className="btn btn-outline" onClick={() => setCreateOpen(false)}>{t('cancel')}</Button><Button type="submit" className="btn btn-primary" disabled={create.isPending}>{t('create')}</Button></div>
                        </form>
                    </DialogPanel>
                </div>
            </Dialog>

            <Dialog open={!!createdToken} onClose={() => setCreatedToken(null)} className="relative z-50">
                <DialogBackdrop className="fixed inset-0 bg-black/30" />
                <div className="fixed inset-0 flex items-center justify-center p-4"><DialogPanel className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
                    <DialogTitle className="text-lg font-semibold text-gray-700">{t('apiTokenCreated')}</DialogTitle>
                    <p className="mt-3 text-sm text-gray-600">{t('content:apiTokenShownOnce', 'This token will be shown only once. Store it in a secure password manager.')}</p>
                    <div className="mt-4 flex gap-2"><Input readOnly value={createdToken?.token || ''} className="input min-w-0 font-mono text-sm" /><Button className="btn btn-outline shrink-0" onClick={copyToken}><ClipboardDocumentIcon className="size-4" />{copied ? t('copied') : t('copy')}</Button></div>
                    <div className="mt-6 flex justify-end"><Button className="btn btn-primary" onClick={() => setCreatedToken(null)}>{t('close')}</Button></div>
                </DialogPanel></div>
            </Dialog>

            <Dialog open={!!tokenToRevoke} onClose={() => setTokenToRevoke(null)} className="relative z-50">
                <DialogBackdrop className="fixed inset-0 bg-black/30" />
                <div className="fixed inset-0 flex items-center justify-center p-4"><DialogPanel className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
                    <DialogTitle className="text-lg font-semibold text-gray-700">{t('revokeApiToken')}</DialogTitle>
                    <p className="mt-3 text-sm text-gray-600">{t('content:revokeApiTokenDescription', { defaultValue: 'Revoking {{name}} will stop any integration using this token.', name: tokenToRevoke?.name })}</p>
                    <form className="mt-5 grid gap-4" onSubmit={submitRevoke}>
                        <Field>
                            <div className="flex items-center gap-1">
                                <Label className="input-label">{t('password')}</Label>
                                <ClickableTooltip
                                    btn={<InformationCircleIcon className="size-4 text-gray-500" aria-label={t('information')} />}
                                    body={t('content:apiTokenPasswordExplanation', 'Your password is required to confirm that you are intentionally creating or revoking an API token. API tokens can grant access to your product data, so this extra check helps protect your account if someone else gains access to your open session.')}
                                />
                            </div>
                            <Input name="password" type="password" required className="input mt-1" autoFocus autoComplete="current-password" />
                        </Field>
                        <div className="flex justify-end gap-3"><Button type="button" className="btn btn-outline" onClick={() => setTokenToRevoke(null)}>{t('cancel')}</Button><Button type="submit" className="btn danger" disabled={revoke.isPending}><TrashIcon className="size-4" />{t('revoke')}</Button></div>
                    </form>
                </DialogPanel></div>
            </Dialog>
        </section>
    );
}

function TokenRow({ token, onRevoke }: { token: AccessToken; onRevoke: () => void }) {
    const expired = isExpired(token.expiresAt);
    return <tr>
        <td className="whitespace-nowrap px-4 py-4 font-medium text-gray-700">{token.name}</td>
        <td className="whitespace-nowrap px-4 py-4 text-gray-500">{formatDate(token.createdAt)}</td>
        <td className="whitespace-nowrap px-4 py-4 text-gray-500">
            {token.expiresAt ? formatDate(token.expiresAt) : t('neverExpires')}
            {expired && <span className="ml-2 text-xs font-medium text-red-600">({t('expired')})</span>}
        </td>
        <td className="whitespace-nowrap px-4 py-4 text-right"><Button className="btn btn-outline danger" onClick={onRevoke}><TrashIcon className="size-4" />{t('revoke')}</Button></td>
    </tr>;
}
